/**
 * USB barcode scanners act as keyboards: they "type" the code very fast and
 * press Enter. This detects such bursts anywhere on the page, so a scan is
 * captured even when the cursor is not in the scan box.
 */
import { useEffect, useRef } from 'react';

export interface ScanBufferOptions {
  /** Shortest code accepted as a scan. */
  minLength?: number;
  /** Largest gap between two scanner keystrokes (people type slower). */
  maxGapMs?: number;
}

export interface KeyLike { key: string; timeStamp: number }

/** Pure state machine, so it can be unit-tested without a DOM. */
export function createScanBuffer(onScan: (code: string) => void, opts: ScanBufferOptions = {}) {
  const minLength = opts.minLength ?? 6;
  const maxGapMs = opts.maxGapMs ?? 50;
  let buf = '';
  let last = 0;
  return {
    /** Feed a keystroke. Returns 'scan' when Enter completes a scan, 'char' when buffered, else 'none'. */
    feed(e: KeyLike): 'scan' | 'char' | 'none' {
      const gap = e.timeStamp - last;
      if (e.key === 'Enter') {
        const code = buf;
        const fast = gap <= maxGapMs * 3;
        buf = '';
        last = 0;
        if (code.length >= minLength && fast) {
          onScan(code);
          return 'scan';
        }
        return 'none';
      }
      if (e.key.length !== 1) return 'none';
      if (buf && gap > maxGapMs) buf = '';
      buf += e.key;
      last = e.timeStamp;
      return 'char';
    },
    get pending() { return buf; },
  };
}

function isEditable(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el) return false;
  const tag = el.tagName;
  return (tag === 'INPUT' && !['checkbox', 'radio', 'button', 'submit'].includes((el as HTMLInputElement).type))
    || tag === 'TEXTAREA';
}

/**
 * Listen for scans on the whole window. Fields marked `data-scan-target`
 * (the scan box) handle their own Enter. If a scan lands in some other field,
 * the scanned characters are removed from it again.
 */
export function useBarcodeScanner(onScan: (code: string) => void, enabled = true) {
  const cb = useRef(onScan);
  cb.current = onScan;
  useEffect(() => {
    if (!enabled) return;
    let target: Element | null = null;
    const scanner = createScanBuffer((code) => {
      if (isEditable(target) && !target.hasAttribute('data-scan-target') && target.value.endsWith(code)) {
        const el = target;
        const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value')?.set;
        setter?.call(el, el.value.slice(0, -code.length));
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
      cb.current(code);
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      target = document.activeElement;
      if (target instanceof HTMLElement && target.hasAttribute('data-scan-target')) return;
      const r = scanner.feed({ key: e.key, timeStamp: e.timeStamp });
      if (r === 'scan') {
        e.preventDefault();
        e.stopPropagation();
      } else if (r === 'char' && !isEditable(target)) {
        e.preventDefault(); // nothing focused: don't let the page react to scan keys
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}
